"use client";

import React, { useRef, useState } from "react";
import Icon from "@/components/ui/Icon";
import { BlogMediaData } from "@/data/blogPosts";

interface BlogVideoPlayerProps {
  media: BlogMediaData;
}

export default function BlogVideoPlayer({ media }: BlogVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  if (!media || !media.url) return null;

  const isYouTube = media.url.includes("youtube.com") || media.url.includes("youtu.be");
  const isVimeo = media.url.includes("vimeo.com");

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <figure className="my-10 rounded-2xl overflow-hidden border border-outline-variant/35 bg-[#120F0D] shadow-2xl">
      {/* Video Header Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#1A1613] border-b border-white/10 text-xs text-neutral-300">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
          <span className="font-mono font-bold tracking-wider uppercase text-[11px] text-primary">
            ARCHITECTURE ANIMATION &amp; WORKFLOW
          </span>
        </div>
        {!isYouTube && !isVimeo && (
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              type="button"
              className="p-1 rounded hover:bg-white/10 transition-colors text-neutral-400 hover:text-white"
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              <Icon name={isMuted ? "shield_alert" : "shield_check"} size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Video Container */}
      <div className="relative aspect-video w-full bg-black flex items-center justify-center">
        {isYouTube ? (
          <iframe
            src={media.url.replace("watch?v=", "embed/")}
            title={media.caption || "Technical Architecture Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        ) : isVimeo ? (
          <iframe
            src={media.url.replace("vimeo.com/", "player.vimeo.com/video/")}
            title={media.caption || "Technical Architecture Video"}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        ) : (
          <div className="relative w-full h-full group">
            <video
              ref={videoRef}
              src={media.url}
              poster={media.poster || "/images/general/asset-1.webp"}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover"
              onClick={togglePlay}
            />
            {/* Play/Pause Overlay indicator */}
            <button
              onClick={togglePlay}
              type="button"
              className="absolute bottom-4 right-4 p-2.5 rounded-xl bg-black/60 hover:bg-black/80 text-white backdrop-blur-md border border-white/20 transition-all opacity-80 group-hover:opacity-100"
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              <Icon name={isPlaying ? "wrench" : "rocket"} size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Caption & Metadata */}
      {media.caption && (
        <figcaption className="px-5 py-3 text-xs sm:text-sm text-neutral-300/80 bg-[#161210] border-t border-white/5 flex items-center gap-2">
          <Icon name="sparkles" size={14} className="text-primary flex-shrink-0" />
          <span>{media.caption}</span>
        </figcaption>
      )}
    </figure>
  );
}
